import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';

import { NotifierList, Template, TemplateNotificationList, User } from '@site-mate/dashpivot-shared-library';

import { UserService } from 'app/user/user.service';

import { usersDivider, variablesDivider } from './work-flow-notification-list-dividers.model';
import { WorkflowNotificationVariableOptions } from './work-flow-notification-variables.model';

@Component({
  selector: 'cc-workflow-notification-list-dropdown',
  templateUrl: 'work-flow-notification-list-dropdown.component.html',
  styleUrls: ['work-flow-notification-list-dropdown.component.scss'],
})
export class WorkflowNotificationListDropdownComponent implements OnChanges {
  private variableOptions = [variablesDivider, ...WorkflowNotificationVariableOptions];

  readonly notifierList = NotifierList;

  @ViewChild('notificationListPopover')
  popover;

  @Input()
  options: any[];

  @Input()
  index: number;

  @Input()
  template: Template;

  @Input()
  canEditNotificationList: boolean;

  @Input()
  placement: 'left' | 'bottom';

  model = [];
  notificationList: TemplateNotificationList[];
  allOptions = [];
  currentUser: User;
  currentUserInList: boolean;

  @Output()
  change = new EventEmitter();

  @Output()
  handleNotificationListClick = new EventEmitter();

  constructor(public readonly userService: UserService) {}

  ngOnInit() {
    this.currentUser = this.userService.getCurrentUser();
    this.currentUserInList = this.isCurrentUserInNotificationList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.template && changes.template.currentValue) {
      this.notificationList = changes.template.currentValue.columns[this.index].notificationList || [];
      this.model = this.notificationList.map((userInList) => {
        if ('notifyRole' in userInList) {
          return this.variableOptions.find(
            (option) => (option.value as NotifierList) === userInList.notifyRole,
          );
        }

        return { label: userInList.fullName, value: userInList.email };
      });
    }

    if (changes && changes.options) {
      this.allOptions = this.getAllOptions();
    }

    this.currentUserInList = this.isCurrentUserInNotificationList();
  }

  getAllOptions() {
    return this.options
      ? [...this.variableOptions, ...this.getOptionsWithCurrentUser()]
      : this.variableOptions;
  }

  private getOptionsWithCurrentUser() {
    const currentUserOption = this.options?.find((option) => option.value === this.currentUser?.email);
    const otherUsersOptions = this.options?.filter((option) => option.value !== this.currentUser?.email);

    const allOptions = currentUserOption ? [currentUserOption, ...otherUsersOptions] : this.options;
    return [usersDivider, ...allOptions];
  }

  private isCurrentUserInNotificationList() {
    return this.notificationList?.some((user) => 'email' in user && user.email === this.currentUser?.email);
  }

  getPopover() {
    return this.popover;
  }

  open() {
    this.getPopover().open();
  }

  close() {
    this.getPopover().close();
  }

  toggle() {
    this.handleNotificationListClick.emit();
    if (this.getPopover().isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  onChangeOption(selectedOptions) {
    const notificationList = selectedOptions
      .map((selectedOption) => {
        const isVariableOption = Object.values(NotifierList).includes(selectedOption.value);

        if (isVariableOption) {
          return {
            notifyRole: selectedOption.value,
          };
        }

        const selected = this.allOptions.find((option) => option.value === selectedOption.value);

        if (selected) {
          return {
            id: selected.id,
            firstName: selected.firstName,
            lastName: selected.lastName,
            fullName: selected.label,
            email: selected.value,
          };
        }

        return null;
      })
      .filter((item) => item);

    this.change.emit({ notificationList, columnIndex: this.index });
  }

  toggleCurrentUserNotification() {
    if (this.currentUserInList) {
      this.onChangeOption(this.model.filter((user) => user.value !== this.currentUser?.email));
    } else {
      this.onChangeOption([
        ...this.model,
        { label: this.currentUser?.fullName, value: this.currentUser?.email },
      ]);
    }
  }
}
